import { CheckoutSection } from "components/basket/checkout-section";
import { BasketItems } from "components/basket/items";
import { NoteSection } from "components/basket/note-section";
import { Layout } from "components/layout";
import { GetPossibleShippingDestinationsDocument } from "lib/graphql-generated";
import { addApolloState, initializeApollo } from "lib/hooks/apollo-client";
import { useUser } from "lib/hooks/use-user";
import { GetServerSideProps } from "next";
import Link from "next/link";
import { Button } from "components/buttons/button";
import { ClientOnly } from "components/client-only";
import { useBasket } from "lib/hooks/basket/use-basket";
import { shippingIsValid } from "lib/shipping-is-valid";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { IBasket } from "lib/basket/types";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PK);

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const client = initializeApollo();
  await client.query({
    query: GetPossibleShippingDestinationsDocument,
  });

  return addApolloState(client, {
    props: {},
  });
};

export const CheckoutPage = () => {
  const { userData } = useUser();
  const [basket, modifyBasket] = useBasket();
  const router = useRouter();
  const [isCheckingOut, setCheckingOut] = useState<boolean>(false);
  let canCheckout =
    shippingIsValid(basket) &&
    (basket.items.length > 0 || (basket.pif !== null && basket.pif !== 0));

  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    canCheckout = shippingIsValid(basket);
  }, [basket]);

  const checkoutButtonDisabled = !canCheckout || isCheckingOut;

  const handleCheckout = async (e) => {
    setCheckingOut(true);
    e.preventDefault();
    const stripe = await stripePromise;
    const response = await fetch("/api/stripe/basket-checkout-session", {
      method: "POST",
      body: JSON.stringify({
        ...basket,
        returnUrl: router.asPath,
      } as IBasket),
    });

    const session = await response.json();
    const error = session.error;
    if (!!session.order) {
      let orderId = session.order;
      router.push(`/thank-you?order_id=${orderId}`);
    } else if (!!session && !error) {
      const result = await stripe.redirectToCheckout({
        sessionId: session.id,
      });
      console.log("error or no session");
      if (result.error) {
        console.log("result error", { result });
        setCheckoutError(result.error.message);
        setCheckingOut(false);
      }
    } else if (error) {
      console.log("error", { error });
      // console.log(JSON.stringify(error, undefined, 2));
      setCheckoutError(error.message);
      if (!!error.invalidItems) {
        error.invalidItems.invalidBooks.forEach((isbn) => {
          let book = basket.items.find((item) => item.id === isbn);
          modifyBasket({
            type: "remove",
            payload: { id: isbn, type: "book", data: { ...book.data } },
          });
        });

        error.invalidItems.invalidProducts.forEach((prodId) => {
          let product = basket.items.find((item) => item.id === prodId);

          modifyBasket({
            type: "remove",
            payload: { id: prodId, type: "product", data: { ...product.data } },
          });
        });
      }
      setCheckingOut(false);
    }
  };

  return (
    <Layout pageName='Checkout' className=' flex flex-col'>
      <article className='mx-auto w-full max-w-screen-2xl py-8 px-4 md:px-8 2xl:px-0'>
        <h1 className='header text-center text-4xl'>Basket & Checkout</h1>
        {!userData && (
          <section className='my-8 space-y-4 text-center'>
            <p>
              Have an account?{" "}
              <Link href='/signin'>
                <a className='underline hover:text-red-default text-green-dark'>
                  Sign in here
                </a>
              </Link>{" "}
              for an easier checkout!
            </p>
            <p>
              <b>Don't</b> have an account?{" "}
              <Link href='/signup'>
                <a className='underline hover:text-red-default text-green-dark'>
                  Create one here
                </a>
              </Link>{" "}
              for an easier checkout in the future!{" "}
            </p>
            <p className='text-gray-700 italic'>
              Don't worry, you'll be able to return to your basket after signing
              in/creating an account
            </p>
          </section>
        )}
        <form className='pt-8 flex flex-col md:flex-row md:space-x-16'>
          <fieldset className='w-full space-y-2'>
            <legend className='w-full py-5 px-2 border-b border-gray-200 text-lg leading-6 font-medium text-white bg-green-default rounded-md '>
              Basket
            </legend>
            <BasketItems />
            <NoteSection />
          </fieldset>
          <fieldset className='w-full flex flex-col'>
            <legend className='w-full py-5 px-2 border-b border-gray-200 text-lg leading-6 font-medium text-white bg-green-default rounded-md mb-2'>
              Checkout
            </legend>
            <ClientOnly>
              <CheckoutSection />
              <Button
                buttonType='secondary'
                buttonSize='xl'
                className='w-full md:w-1/2 ml-auto mt-4'
                disabled={checkoutButtonDisabled}
                onClick={handleCheckout}>
                {isCheckingOut ? "Checking out" : "Checkout"}
              </Button>
            </ClientOnly>
          </fieldset>
        </form>
      </article>
    </Layout>
  );
};

export default CheckoutPage;
